import { LitElement, html, css } from "lit";
import { customElement, state, query } from "lit/decorators.js";

import "@material/web/progress/circular-progress.js";
import "@material/web/button/filled-button.js";
import "@material/web/button/text-button.js";
import "@material/web/textfield/outlined-text-field.js";
import "@material/web/select/outlined-select.js";
import "@material/web/select/select-option.js";
import "@material/web/dialog/dialog.js";
import "@material/web/icon/icon.js";
import type { MdDialog } from "@material/web/dialog/dialog.js";

import "../components/app-shell";
import { api } from "../lib/api";
import { currentUser, hasRole } from "../lib/pb";
import { getOrganization } from "../lib/org-store";
import { sharedStyles } from "../styles/shared";
import { inputValue, errorMessage } from "../lib/dom";
import { icon } from "../lib/icons";
import { toast } from "../lib/toast";
import {
  ROLE_LABELS,
  type Invitation,
  type MemberRole,
  type Membership,
  type Organization,
} from "../lib/types";

const ROLES = Object.keys(ROLE_LABELS) as MemberRole[];

@customElement("members-page")
export class MembersPage extends LitElement {
  static styles = [
    sharedStyles,
    css`
      form.dialog-form {
        display: flex;
        flex-direction: column;
        gap: 16px;
        padding-top: 8px;
        min-width: min(360px, 80vw);
      }

      .member {
        display: flex;
        align-items: center;
        gap: 12px;
      }

      /* Initial avatars cycle through the M3 accent containers (design). */
      .avatar {
        width: 40px;
        height: 40px;
        border-radius: 999px;
        display: inline-flex;
        align-items: center;
        justify-content: center;
        flex: none;
        font: 500 14px/1 var(--font-plain, sans-serif);
        text-transform: uppercase;
      }

      .avatar--0 {
        background: var(--md-sys-color-primary-container);
        color: var(--md-sys-color-on-primary-container);
      }

      .avatar--1 {
        background: var(--md-sys-color-secondary-container);
        color: var(--md-sys-color-on-secondary-container);
      }

      .avatar--2 {
        background: var(--md-sys-color-tertiary-container);
        color: var(--md-sys-color-on-tertiary-container);
      }
    `,
  ];

  @state() private org: Organization | null = null;
  @state() private members: Membership[] = [];
  @state() private invitations: Invitation[] = [];
  @state() private loading = true;
  @state() private error = "";

  @state() private inviteEmail = "";
  @state() private inviteRole: MemberRole = "editor";
  @state() private inviting = false;
  @state() private inviteError = "";

  @query("#invite-dialog") private dialog?: MdDialog;

  private get orgId(): string {
    return currentUser()?.organization ?? "";
  }

  private get isAdmin(): boolean {
    // The backend role is org_admin — there is no "admin" role, and testing for
    // one silently denied every org admin this page's controls.
    return hasRole("org_admin");
  }

  connectedCallback(): void {
    super.connectedCallback();
    void this.load();
  }

  private async load(): Promise<void> {
    this.loading = true;
    this.error = "";
    try {
      const orgId = this.orgId;
      const [org, members] = await Promise.all([
        getOrganization(),
        api.get<Membership[]>(`/tenancy/organizations/${orgId}/members`),
      ]);
      this.org = org;
      this.members = members ?? [];

      if (this.isAdmin) {
        // The typed route, NOT a raw collection read: the service strips the
        // token server-side ("never leaves the module, not even as a hash"),
        // so no token form crosses the wire at all. A usable invite link only
        // ever exists in the response of the POST that minted it (or a
        // re-send, which rotates and returns a fresh plaintext).
        this.invitations =
          (await api.get<Invitation[] | null>(
            `/tenancy/organizations/${orgId}/invitations`,
          )) ?? [];
      }
    } catch (err) {
      this.error = errorMessage(err, "Failed to load team.");
    } finally {
      this.loading = false;
    }
  }

  // The one role-option list, shared by the invite dialog and the per-row
  // role select so the two can never drift.
  private roleOptions() {
    return ROLES.map(
      (role) => html`
        <md-select-option value=${role}>
          <div slot="headline">${ROLE_LABELS[role]}</div>
        </md-select-option>
      `,
    );
  }

  private openInvite(): void {
    this.inviteEmail = "";
    this.inviteRole = "editor";
    this.inviteError = "";
    this.dialog?.show();
  }

  private async handleInvite(e: Event): Promise<void> {
    e.preventDefault();
    const orgId = this.orgId;
    if (!orgId) return;

    this.inviteError = "";
    this.inviting = true;
    try {
      const invitation = await api.post<Invitation>(
        `/tenancy/organizations/${orgId}/invitations`,
        { email: this.inviteEmail, role: this.inviteRole },
      );
      this.invitations = [invitation, ...this.invitations];
      this.dialog?.close();
      toast(`Invitation sent to ${invitation.Email}.`);
    } catch (err) {
      this.inviteError = errorMessage(err, "Failed to send invitation.");
    } finally {
      this.inviting = false;
    }
  }

  // Change a member's role. The server owns the invariants (an org must keep
  // at least one admin; only valid roles), so refusals are surfaced verbatim —
  // "must keep at least one admin" is an answer, not a failure.
  private async changeRole(m: Membership, role: MemberRole): Promise<void> {
    const orgId = this.orgId;
    if (!orgId || role === m.Role) return;
    try {
      const updated = await api.patch<Membership>(
        `/tenancy/organizations/${orgId}/members/${m.UserID}`,
        { role },
      );
      this.members = this.members.map((x) =>
        x.UserID === updated.UserID ? updated : x,
      );
      toast(`Role changed to ${ROLE_LABELS[updated.Role]}.`);
    } catch (err) {
      toast(errorMessage(err, "Could not change the role."), true);
      this.members = [...this.members]; // snap the select back to the stored role
    }
  }

  // Remove a member. This is the control whose ABSENCE was the finding: the
  // backend has revoked access correctly for a while, but the UI never called
  // it, so a leaver kept editor/admin access unless somebody knew curl.
  private async removeMember(m: Membership): Promise<void> {
    const orgId = this.orgId;
    if (!orgId) return;
    if (
      !window.confirm(
        "Remove this member? They lose access to products, passports and NFC batches immediately.",
      )
    ) {
      return;
    }
    try {
      await api.del(`/tenancy/organizations/${orgId}/members/${m.UserID}`);
      this.members = this.members.filter((x) => x.UserID !== m.UserID);
      toast("Member removed.");
    } catch (err) {
      toast(errorMessage(err, "Could not remove the member."), true);
    }
  }

  // Re-send rotates the token and emails a fresh link (the stored token is a
  // hash, so re-sending is the ONLY way to produce a usable link again).
  private async resendInvite(inv: Invitation): Promise<void> {
    const orgId = this.orgId;
    if (!orgId) return;
    try {
      const fresh = await api.post<Invitation>(
        `/tenancy/organizations/${orgId}/invitations`,
        { email: inv.Email, role: inv.Role },
      );
      // Matched by ID, not email: the server reuses the row on a re-send, so
      // the IDs agree — and the row's own identity does not depend on a
      // uniqueness rule that lives two layers away.
      this.invitations = this.invitations.map((x) =>
        x.ID === fresh.ID ? fresh : x,
      );
      toast(`Invitation re-sent to ${fresh.Email}.`);
    } catch (err) {
      toast(errorMessage(err, "Could not re-send the invitation."), true);
    }
  }

  private async revokeInvite(inv: Invitation): Promise<void> {
    try {
      await api.del(`/tenancy/invitations/${inv.ID}`);
      this.invitations = this.invitations.filter((x) => x.ID !== inv.ID);
      toast(`Invitation to ${inv.Email} revoked.`);
    } catch (err) {
      toast(errorMessage(err, "Could not revoke the invitation."), true);
    }
  }

  private inviteLink(token: string): string {
    return `${window.location.origin}/onboarding?invite=${token}`;
  }

  private async copyInviteLink(token: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(this.inviteLink(token));
      toast("Invite link copied to clipboard.");
    } catch {
      toast("Could not copy invite link.", true);
    }
  }

  // Role cell: admins get a live select on everyone but themselves; everyone
  // else sees a badge. Kept out of the row template — the nesting there was
  // already three ternaries deep.
  private renderRoleCell(m: Membership, self: boolean) {
    if (!this.isAdmin || self) {
      return html`<span class="badge badge--neutral"
        >${ROLE_LABELS[m.Role]}</span
      >`;
    }
    return html`
      <md-outlined-select
        .value=${m.Role}
        @change=${(e: Event) =>
          void this.changeRole(m, inputValue(e) as MemberRole)}
      >
        ${this.roleOptions()}
      </md-outlined-select>
    `;
  }

  private renderMemberActions(m: Membership, self: boolean) {
    if (self) return null;
    return html`<md-text-button @click=${() => void this.removeMember(m)}>
      Remove
    </md-text-button>`;
  }

  render() {
    const me = currentUser();
    return html`
      <app-shell active="/members">
        <div class="page">
          <div class="page-header">
            <div>
              <h1>Team</h1>
              <p>
                People with access to
                ${this.org?.Name ?? "your organization"}'s Digital Product
                Passports.
              </p>
            </div>
            ${this.isAdmin
              ? html`<md-filled-button @click=${() => this.openInvite()}>
                  Invite member
                  <span slot="icon">${icon("person_add")}</span>
                </md-filled-button>`
              : null}
          </div>

          ${this.loading
            ? html`<div class="center-loading">
                <md-circular-progress indeterminate></md-circular-progress>
              </div>`
            : this.error
              ? html`<p class="error-text">${this.error}</p>`
              : html`
                  <div class="card card--flush">
                    <div class="cardhead">
                      <h2>Members <small>· ${this.members.length}</small></h2>
                    </div>
                    <div class="table-scroll"><table>
                      <thead>
                        <tr>
                          <th>Member</th>
                          <th>Role</th>
                          <th>Joined</th>
                          ${this.isAdmin ? html`<th></th>` : null}
                        </tr>
                      </thead>
                      <tbody>
                        ${this.members.map(
                          (m, i) => html`
                            <tr>
                              <td>
                                <div class="member">
                                  <span class="avatar avatar--${i % 3}"
                                    >${(m.UserID === me?.id
                                      ? me?.display_name || me?.email || m.UserID
                                      : m.UserID
                                    ).slice(0, 2)}</span
                                  >
                                  <span>
                                    <b
                                      >${m.UserID === me?.id
                                        ? me?.display_name || me?.email
                                        : html`<code>${m.UserID}</code>`}</b
                                    >
                                    ${m.UserID === me?.id
                                      ? html`<small>you</small>`
                                      : null}
                                  </span>
                                </div>
                              </td>
                              <td>
                                ${this.renderRoleCell(m, m.UserID === me?.id)}
                              </td>
                              <td>
                                ${new Date(m.CreatedAt).toLocaleDateString()}
                              </td>
                              ${this.isAdmin
                                ? html`<td>
                                    ${this.renderMemberActions(
                                      m,
                                      m.UserID === me?.id,
                                    )}
                                  </td>`
                                : null}
                            </tr>
                          `,
                        )}
                      </tbody>
                    </table></div>
                  </div>

                  ${this.isAdmin
                    ? html`
                        <div class="card">
                          <h2>Pending invitations</h2>
                          ${this.invitations.length === 0
                            ? html`<p class="muted">
                                No pending invitations.
                              </p>`
                            : html`
                                <div class="table-scroll"><table>
                                  <thead>
                                    <tr>
                                      <th>Email</th>
                                      <th>Role</th>
                                      <th>Expires</th>
                                      <th></th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    ${this.invitations.map(
                                      (inv) => html`
                                        <tr>
                                          <td>${inv.Email}</td>
                                          <td>${ROLE_LABELS[inv.Role]}</td>
                                          <td>
                                            ${new Date(
                                              inv.ExpiresAt,
                                            ).toLocaleDateString()}
                                          </td>
                                          <td>
                                            ${inv.Token
                                              ? html`<md-text-button
                                                  @click=${() =>
                                                    void this.copyInviteLink(
                                                      inv.Token,
                                                    )}
                                                >
                                                  Copy invite link
                                                </md-text-button>`
                                              : null}
                                            <md-text-button
                                              @click=${() =>
                                                void this.resendInvite(inv)}
                                            >
                                              Re-send
                                            </md-text-button>
                                            <md-text-button
                                              @click=${() =>
                                                void this.revokeInvite(inv)}
                                            >
                                              Revoke
                                            </md-text-button>
                                          </td>
                                        </tr>
                                      `,
                                    )}
                                  </tbody>
                                </table></div>
                              `}
                        </div>
                      `
                    : null}
                `}
        </div>

        <md-dialog id="invite-dialog">
          <div slot="headline">Invite a team member</div>
          <form
            slot="content"
            id="invite-form"
            class="dialog-form"
            @submit=${(e: Event) => void this.handleInvite(e)}
          >
            <md-outlined-text-field
              label="Email address"
              type="email"
              required
              .value=${this.inviteEmail}
              @input=${(e: Event) => (this.inviteEmail = inputValue(e))}
            ></md-outlined-text-field>
            <md-outlined-select
              label="Role"
              .value=${this.inviteRole}
              @change=${(e: Event) =>
                (this.inviteRole = inputValue(e) as MemberRole)}
            >
              ${this.roleOptions()}
            </md-outlined-select>
            ${this.inviteError
              ? html`<p class="error-text">${this.inviteError}</p>`
              : null}
          </form>
          <div slot="actions">
            <md-text-button @click=${() => this.dialog?.close()}>
              Cancel
            </md-text-button>
            <md-filled-button
              form="invite-form"
              type="submit"
              ?disabled=${this.inviting}
            >
              ${this.inviting ? "Sending…" : "Send invite"}
            </md-filled-button>
          </div>
        </md-dialog>
      </app-shell>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "members-page": MembersPage;
  }
}
